import type { ReactNode } from 'react';
import { V4 } from '@components/v4/tokens';
import { IconBuildingBank, IconCheck, IconCreditCard } from '@tabler/icons-react';
import type { MetodoPago } from '../hooks/useActivatePlan';

interface PaymentMethodSelectorProps {
  value: MetodoPago;
  onChange: (metodo: MetodoPago) => void;
  disabled?: boolean;
}

interface OptionDef {
  id: MetodoPago;
  title: string;
  description: string;
  icon: ReactNode;
}

const OPTIONS: OptionDef[] = [
  {
    id: 'transferencia',
    title: 'Transferencia bancaria',
    description: 'Te enviamos una factura con los datos para pagar por transferencia (3 días para pagar).',
    icon: <IconBuildingBank size={22} stroke={1.8} />,
  },
  {
    id: 'tarjeta',
    title: 'Tarjeta de crédito o débito',
    description: 'Cobro automático cada periodo. Pago seguro con verificación 3D Secure.',
    icon: <IconCreditCard size={22} stroke={1.8} />,
  },
];

// PRD-014 FR-28 — Selector de método de pago al activar/cambiar de plan.
// Solo dos opciones (tarjeta | transferencia), sin texto libre. Default: transferencia.
// V4: 100% inline styles con tokens (sin clases Tailwind).
export const PaymentMethodSelector = ({ value, onChange, disabled = false }: PaymentMethodSelectorProps) => {
  return (
    <div role="radiogroup" aria-label="Método de pago" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {OPTIONS.map((option) => {
        const selected = value === option.id;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(option.id)}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              width: '100%',
              textAlign: 'left',
              padding: 14,
              borderRadius: 10,
              border: `1.5px solid ${selected ? V4.secondary : V4.grayLight}`,
              background: selected ? '#F4FBFC' : V4.white,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              transition: 'border-color 150ms, background 150ms',
              fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => {
              if (!disabled && !selected) (e.currentTarget as HTMLElement).style.borderColor = V4.secondaryLight2;
            }}
            onMouseLeave={(e) => {
              if (!disabled && !selected) (e.currentTarget as HTMLElement).style.borderColor = V4.grayLight;
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: 40,
                height: 40,
                borderRadius: 8,
                background: selected ? V4.secondary : V4.grayUltraLight,
                color: selected ? V4.white : V4.secondary,
              }}
            >
              {option.icon}
            </span>

            <span style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 700,
                  color: V4.primary,
                  marginBottom: 2,
                }}
              >
                {option.title}
              </span>
              <span style={{ display: 'block', fontSize: 12, lineHeight: 1.4, color: V4.grayUltraDark }}>
                {option.description}
              </span>
            </span>

            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: 22,
                height: 22,
                borderRadius: 9999,
                border: `2px solid ${selected ? V4.secondary : V4.gray}`,
                background: selected ? V4.secondary : 'transparent',
                color: V4.white,
              }}
            >
              {selected && <IconCheck size={14} stroke={3} />}
            </span>
          </button>
        );
      })}
    </div>
  );
};
